import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AddStateService } from 'src/app/add-state-service/add-state.service';
import { ShoppingCartService } from 'src/app/shopping-cart/shopping-cart.service';
import { TrimFormControl } from '../trim-control';
import { RequestorModel } from 'src/app/shopping-cart/requestor-model.model';
import { CountryService } from 'src/app/country-service/country.service';
import { Subscription, noop } from 'rxjs';
import { RecaptchaComponent } from 'ng-recaptcha';
import { ReqSubmitService } from 'src/app/submission/req-submit-service';
import { KeyValue } from '@angular/common';
import { FilePostService } from 'src/app/submission/file-post.service';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
  providers: [ReqSubmitService, FilePostService]
})
export class ContactComponent implements OnInit, OnDestroy {
  @ViewChild(RecaptchaComponent) captcha: RecaptchaComponent;
  private _contactForm: FormGroup;
  private _states: Map<string, string> = new Map<string, string>();
  private _countries: Map<string, string> = new Map<string, string>();

  private _requestor: RequestorModel = new RequestorModel();
  private _submitText = 'Submit My Request(s)';
  private _submitted = false;
  private subscriptions: Subscription[] = new Array<Subscription>();

  constructor(
    private stateService: AddStateService,
    private cartService: ShoppingCartService,
    private countryService: CountryService,
    private submitServ: ReqSubmitService
  ) {
    this.stateService.setupStateServiceCall();
    this.countryService.getCountries();
    this._states = stateService.stateMap;
    this.countries = countryService.countries;
  }


  ngOnInit() {
    this.contactForm = new FormGroup({
      contactName: new TrimFormControl('', [
        Validators.required,
        Validators.pattern(/^[A-Za-z0-9\&\-\,\.\/\:\#\'\(\) ]+$/)
      ]),
      contactMiddleInitial: new TrimFormControl('', [
        Validators.pattern(/^[A-Za-z0-9\&\-\,\.\/\:\#\'\(\) ]+$/)
      ]),
      contactLastName: new TrimFormControl('', [
        Validators.required,
        Validators.pattern(/^[A-Za-z0-9\&\-\,\.\/\:\#\'\(\) ]+$/)
      ]),
      contactCountry: new FormControl('US', [Validators.required]),
      contactState: new FormControl('', Validators.required),
      contactPhone: new FormControl('', [Validators.required]),
      contactFax: new FormControl(''),
      contactEmail: new FormControl('', [Validators.email]),
      contactSignature: new FormControl('', [Validators.requiredTrue]),
      contactCapcha: new FormControl(null, Validators.required)
    });

    this.contactForm.controls['contactEmail'].markAsTouched();
    this.subscriptions.push(
      this.contactForm.controls['contactCountry'].valueChanges.subscribe(
        selectedValue => {
          if (selectedValue === 'US') {
            if (this.contactForm.get('contactState') === null) {
              this.contactForm.addControl(
                'contactState',
                new FormControl('', [Validators.required])
              );
            }
          }
          else {
            if (this.contactForm.get('contactState') !== null) {
              this.contactForm.removeControl('contactState');
            }
          }
        }
      )
    );
  }

  exit(): void {
    if (window.confirm('Changes you made will not be saved.')) {
      this.cartService.clearCart();
    }
  }

  submit(): void {
    const ph = this.contactForm.controls['contactPhone'].value;
    const requestorPhoneRegion = ph[0] === '+' ? 'International' : 'Domestic';
    const newPh = ph.replace('(', '' ).replace(')', '').replace('-', '').replace(' ', '');
    
    let fx = this.contactForm.controls['contactFax'].value;
    fx = fx === '+' ? null : fx;
    const newFx = fx ? fx.replace('(', '' ).replace(')', '').replace('-', '').replace(' ', '') : '';
    
    const requestorFaxRegion = fx ? fx[0] === '+' ? 'International' : 'Domestic' : null;
    
    const tempDate = this.requestor.capchaDate;
    this.requestor = this.contactForm.value;
    this.requestor.requestorPhoneRegion = requestorPhoneRegion;
    this.requestor.requestorFaxRegion = requestorFaxRegion;
    this.requestor.capchaDate = tempDate;
    this.requestor.contactPhone = newPh;
    this.requestor.contactFax = newFx;

    this.requestor.contactFullName = this._contactForm.controls['contactName'].value + ' ' +
      (this.contactForm.controls['contactMiddleInitial'].value ? 
      this.contactForm.controls['contactMiddleInitial'].value + ' ' : 
      '') 
      + this.contactForm.controls['contactLastName'].value;

    this.requestor.contactRequests = this.cartService.getCart();
    this.submitted = true;
    this.submitText = 'Submitting...';
    this.submitServ.submitRequest(this.requestor);
  }

  public handleCorrectCaptcha(event) {
    this.contactForm.controls['contactCapcha'].setValue(event);
    this.contactForm.controls['contactCapcha'].updateValueAndValidity();
    this._requestor.capchaDate = new Date();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  public onCountryChange(event): void {
    if (this.contactForm.controls['contactCountry'].value !== 'US') {
      this.contactForm.controls['contactPhone'].patchValue('+');
      this.contactForm.controls['contactFax'].patchValue('+');
      this.contactForm.controls['contactFax'].setValidators(Validators.maxLength(1));
    }
    else {
      this.contactForm.controls['contactPhone'].value === '+' ?
      this.contactForm.controls['contactPhone'].patchValue('') :
      noop();

      this.contactForm.controls['contactFax'].value === '+' ?
      this.contactForm.controls['contactFax'].patchValue('') :
      noop();
    }

    this.contactForm.controls['contactPhone'].updateValueAndValidity();
    this.contactForm.controls['contactFax'].updateValueAndValidity();
  }

  valueAscOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
    return a.value.localeCompare(b.value);
  }

  public set contactForm(contactForm: FormGroup) {
    this._contactForm = contactForm;
  }

  public get contactForm(): FormGroup {
    return this._contactForm;
  }

  public get states() {
    return this._states;
  }

  public get submitText() {
    return this._submitText;
  }

  public set submitText(value) {
    this._submitText = value;
  }

  public get submitted() {
    return this._submitted;
  }

  public set submitted(value) {
    this._submitted = value;
  }

  public get requestor(): RequestorModel {
    return this._requestor;
  }

  public set requestor(value: RequestorModel) {
    this._requestor = value;
  }

  public get countries(): Map<string, string> {
    return this._countries;
  }

  public set countries(value: Map<string, string>) {
    this._countries = value;
  }
}
